// src/pages/RegisterPage.tsx
import React, { Suspense } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext'; 

// --- Helper Components ---
const FormInput = ({ name, type = 'text', label, placeholder }: any) => (
    <div>
        <label htmlFor={name} className="sr-only">{label}</label>
        <Field
            id={name} name={name} type={type} required
            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder={placeholder}
        />
        <ErrorMessage name={name} component="p" className="text-red-400 text-xs italic mt-1" />
    </div>
);

// --- Main Page Component ---
const RegisterPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { showNotification } = useNotification(); 

    const validationSchema = Yup.object({
        username: Yup.string().required(t('validation.required')),
        email: Yup.string().email(t('validation.emailInvalid')).required(t('validation.required')),
        password: Yup.string().min(6, t('validation.passwordLength')).required(t('validation.required')),
    });

    const handleSubmit = async (values: any, { setSubmitting }: FormikHelpers<any>) => {
        try {
            const config = { headers: { 'Accept-Language': i18n.language } };
            const response = await api.post('/Auth/register', values, config);

            // Use our custom notification instead of alert()
            showNotification(response.data.message || t('register.successMessage'), 'success');
            navigate('/login');
        } catch (error: any) {
            const errorMessage = typeof error.response?.data === 'string' ? error.response.data : t('register.errorMessage');
            // Use our custom notification for errors
            showNotification(errorMessage, 'error');
            setSubmitting(false);
        }
    };

    return (
        <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-gray-800 p-10 rounded-xl shadow-lg">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
                        {t('register.title')}
                    </h2>
                </div>
                <Formik
                    initialValues={{ username: '', email: '', password: '' }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting }) => (
                        <Form className="mt-8 space-y-6">
                            <div className="rounded-md shadow-sm space-y-4">
                                <FormInput name="username" label={t('common.username')} placeholder={t('common.username')} />
                                <FormInput name="email" type="email" label={t('common.email')} placeholder={t('common.email')} />
                                <FormInput name="password" type="password" label={t('common.password')} placeholder={t('common.password')} />
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
                                >
                                    {isSubmitting ? t('common.loading') : t('register.submitButton')}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
                <div className="text-sm text-center">
                    <Link to="/login" className="font-medium text-indigo-400 hover:text-indigo-300">
                        Masz już konto? Zaloguj się.
                    </Link>
                </div>
            </div>
        </div>
    );
};

// --- Wrapper Component ---
const RegisterPage = () => (
    <Suspense fallback={<div className="text-center p-8 text-white">Ładowanie...</div>}>
        <RegisterPageContent />
    </Suspense>
);

export default RegisterPage;